import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { NgxPaginationModule } from 'ngx-pagination';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DashboardCardComponent } from './pages/dashboard/dashboard-card/dashboard-card.component';
import { ControlMessagesComponent } from './shared/components/control-messages/control-messages.component';
import { MultiSelectMultiLevelComponent } from './shared/components/multi-select-multi-level/multi-select-multi-level.component';
import { MultiSelectMultiLevelItemComponent } from './shared/components/multi-select-multi-level-item/multi-select-multi-level-item.component';
import { ConfirmationModalComponent } from './shared/components/confirmation-modal/confirmation-modal.component';
import { DeactiveModalComponent } from './shared/components/deactive-modal/deactive-modal.component';
import { ReactiveModalComponent } from './shared/components/reactive-modal/reactive-modal.component';
import { ConfirmationCreatePackageModalComponent } from './shared/components/confirmation-create-package-modal/confirmation-create-package-modal.component';
import { FieldValidationModule } from './shared/components/field-validation/field-validation.module';
import { TrimDirective } from './shared/directives/trim.directive';
import { CopyClipboardDirective } from './shared/directives/copy.directive';
import { RequestPipe } from './shared/pipes/request.pipe';
import { SortArrayPipe } from './shared/pipes/sortArray.pipe';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { LoadingInterceptor } from './core/interceptors/loading.interceptor';
import { ToastInterceptor } from './core/interceptors/toast.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';

import { FitListComponent } from './pages/fits/fit-list/fit-list.component';
import { FitErrorsComponent } from './pages/fits/fit-errors/fit-errors.component';
import { OrderListComponent } from './pages/made4u/orders/order-list/order-list.component';
import { OrderActionsComponent } from './pages/made4u/orders/order-actions/order-actions.component';
import { ScanListComponent } from './pages/made4u/scans/scan-list/scan-list.component';
import { ScanErrorsComponent } from './pages/made4u/scans/scan-errors/scan-errors.component';
import { SettingListComponent } from './pages/settings/setting-list/setting-list.component';
import { EntityListComponent } from './pages/entities/entity-list/entity-list.component';
import { EntityActionsComponent } from './pages/entities/entity-actions/entity-actions.component';
import { DeviceListComponent } from './pages/devices/device-list/device-list.component';
import { DeviceActionsComponent } from './pages/devices/device-actions/device-actions.component';
import { LoginComponent } from './pages/login/login.component';
import { Setting360Component } from './pages/settings/setting-360/setting-360.component';
import { SettingMimesysComponent } from './pages/settings/setting-mimesys/setting-mimesys.component';
import { SettingBackofficeComponent } from './pages/settings/setting-backoffice/setting-backoffice.component';
import { SettingFitmade4uComponent } from './pages/settings/setting-fitmade4u/setting-fitmade4u.component';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { PreviewExternalSettingsComponent } from './core/components/preview-external-settings/preview-external-settings.component';
import { UserListComponent } from './pages/users/user-list/user-list.component';
import { UserActionsComponent } from './pages/users/user-actions/user-actions.component';
import { HorizonsTeamListComponent } from './pages/users/horizons-team-list/horizons-team-list.component';
import { HorizonsTeamActionsComponent } from './pages/users/horizons-team-actions/horizons-team-actions.component';
import { TeamListComponent } from './pages/users/team-list/team-list.component';
import { TeamActionsComponent } from './pages/users/team-actions/team-actions.component';
import { GazeMapListComponent } from './pages/gaze-maps/gaze-map-list/gaze-map-list.component';
import { GazeMapActionsComponent } from './pages/gaze-maps/gaze-map-actions/gaze-map-actions.component';
import { PackageListComponent } from './pages/licenses/package-list/package-list.component';
import { PackageActionsComponent } from './pages/licenses/package-actions/package-actions.component';
import { PackageTypeListComponent } from './pages/licenses/package-type-list/package-type-list.component';
import { PackageTypeActionsComponent } from './pages/licenses/package-type-actions/package-type-actions.component';
import { PolicyListComponent } from './pages/licenses/policy-list/policy-list.component';
import { PolicyActionsComponent } from './pages/licenses/policy-actions/policy-actions.component';
import { SingleSelectMultiLevelComponent } from './shared/components/single-select-multi-level/single-select-multi-level.component';
import { SingleSelectMultiLevelItemComponent } from './shared/components/single-select-multi-level-item/single-select-multi-level-item.component';
import { DatatableSmartComponent } from './shared/components/datatable-smart/datatable-smart.component';
import { ReassignModalComponent } from './shared/components/reassign-modal/reassign-modal.component';
import { NoDeviceReassignModalComponent } from './shared/components/no-device-reassign-modal/no-device-reassign-modal.component';
import { CorridorModalComponent } from './shared/components/corridor-modal/corridor-modal.component';
import { VideoModalComponent } from './shared/components/video-modal/video-modal.component';
import { DdbbUtils } from './shared/helpers/utils';
import { EntitySelectorComponent } from './shared/components/entity-selector/entity-selector.component';
import { TreeComponent } from './shared/components/tree/tree.component';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkTreeModule } from '@angular/cdk/tree';
import { BasicVirtualScrollComponent } from './shared/components/basic-virtual-scroll/basic-virtual-scroll.component';
import { ProgressModalComponent } from './shared/components/progress-modal/progress-modal.component';
import { RegularProgressModalComponent } from './shared/components/regular-progress-modal/regular-progress-modal.component';
import { AdminToolsComponent } from './shared/admin-tools/admin-tools.component';
import { GlobalPipe, ToNumberPipe, ToStringPipe } from './shared/pipes/global.pipe';
import { TranslateMimesysPipe } from './shared/pipes/pipes/translate-mimesys.pipe';
import { BgProcessesContainerModule } from './shared/components/bg-processes-container/bg-processes-container.module';
import { FieldInfoIconModule } from './shared/components/field-info-icon/field-info-icon.module';
import { TooltipComponent } from './shared/components/tooltip/tooltip.component';
import { AnalyticsVrresultsComponent } from './pages/analytics/analytics-vrresults/analytics-vrresults.component';
import { AnalyticsPackagesComponent } from './pages/analytics/analytics-packages/analytics-packages.component';
import { AnalyticsFitComponent } from './pages/analytics/analytics-fit/analytics-fit.component';
import { AnalyticsFiltersComponent } from './pages/analytics/analytics-filters/analytics-filters.component';
import { AnalyticsTableComponent } from './pages/analytics/analytics-table/analytics-table.component';
import { ColumnTranslatePipe } from './shared/pipes/column-translate.pipe';


export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    // This returns the text to show if the key isn't found
    return params.key; 
  }
}

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [
    AppComponent,
    PageNotFoundComponent,
    DashboardComponent,
    DashboardCardComponent,
    FitListComponent,
    DatatableSmartComponent,
    ControlMessagesComponent,
    MultiSelectMultiLevelComponent,
    MultiSelectMultiLevelItemComponent,
    ConfirmationModalComponent,
    DeactiveModalComponent,
    ReactiveModalComponent,
    ConfirmationCreatePackageModalComponent,
    TrimDirective,
    CopyClipboardDirective,
    RequestPipe,
    SortArrayPipe,
    FitErrorsComponent,
    OrderListComponent,
    OrderActionsComponent,
    ScanListComponent,
    ScanErrorsComponent,
    SettingListComponent,
    EntityListComponent,
    EntityActionsComponent,
    DeviceListComponent,
    DeviceActionsComponent,
    LoginComponent,
    Setting360Component,
    SettingMimesysComponent,
    SettingBackofficeComponent,
    SettingFitmade4uComponent,
    LoadingComponent,
    PreviewExternalSettingsComponent,
    UserListComponent,
    UserActionsComponent,
    HorizonsTeamListComponent,
    HorizonsTeamActionsComponent,
    TeamListComponent,
    TeamActionsComponent,
    GazeMapListComponent,
    GazeMapActionsComponent,
    PackageListComponent,
    PackageActionsComponent,
    PackageTypeListComponent,
    PackageTypeActionsComponent,
    PolicyListComponent,
    PolicyActionsComponent,
    SingleSelectMultiLevelComponent,
    SingleSelectMultiLevelItemComponent,
    ReassignModalComponent,
    NoDeviceReassignModalComponent,
    CorridorModalComponent,
    VideoModalComponent,
    EntitySelectorComponent,
    TreeComponent,
    BasicVirtualScrollComponent,
    ProgressModalComponent,
    RegularProgressModalComponent,
    AdminToolsComponent,
    GlobalPipe, ToNumberPipe, ToStringPipe, TranslateMimesysPipe,
    TooltipComponent,
    AnalyticsVrresultsComponent,
    AnalyticsPackagesComponent,
    AnalyticsFitComponent,
    AnalyticsFiltersComponent,
    AnalyticsTableComponent,
    ColumnTranslatePipe
  ],
  exports:[ColumnTranslatePipe],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    TranslateModule.forRoot({
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      },
      defaultLanguage: 'en-GB'
    }),
    ToastrModule.forRoot(),
    NgxPaginationModule,
    AppRoutingModule,
    MatTreeModule,
    MatIconModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    ScrollingModule,
    CdkTreeModule,  // Añadirlo a los imports,
    BgProcessesContainerModule,
    FieldInfoIconModule,
    FieldValidationModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ToastInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: DdbbUtils, useClass: DdbbUtils },
    { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }


